import { useState, useEffect, useCallback } from "react";
import { Menu, X } from "lucide-react";

const navLinks = [
  { label: "Services", href: "#services" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "About Us", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = useCallback((e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, []);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md shadow-sm border-b border-[#E2E2E0]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        {/* Logo — uniform style */}
        <a
          href="#hero"
          onClick={(e) => handleNavClick(e, "#hero")}
          data-testid="navbar-logo"
          className={`font-heading text-[22px] md:text-[26px] font-semibold tracking-tight transition-colors duration-500 ${
            scrolled ? "text-charcoal" : "text-white"
          }`}
        >
          Giri Interiors and Constructions
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`font-body text-sm font-medium transition-colors duration-300 relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-gold after:transition-all after:duration-300 hover:after:w-full ${
                scrolled
                  ? "text-[#555555] hover:text-gold"
                  : "text-white/80 hover:text-gold"
              }`}
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, "#contact")}
            data-testid="nav-cta-button"
            className={`font-body text-sm font-medium px-6 py-2.5 transition-all duration-300 hover:-translate-y-[1px] hover:shadow-lg ${
              scrolled
                ? "bg-charcoal text-white hover:bg-charcoal-light"
                : "bg-gold text-charcoal hover:bg-gold-light"
            }`}
          >
            Get a Free Quote
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden p-2 transition-colors duration-500 ${scrolled ? "text-charcoal" : "text-white"}`}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`md:hidden overflow-hidden transition-all duration-500 ease-in-out bg-white/95 backdrop-blur-md ${
          mobileOpen ? "max-h-[400px] border-b border-[#E2E2E0]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-body text-base font-medium text-[#555555] hover:text-gold transition-colors"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, "#contact")}
            data-testid="mobile-cta-button"
            className="font-body text-sm font-medium bg-charcoal text-white px-6 py-3 text-center hover:bg-charcoal-light transition-all duration-300"
          >
            Get a Free Quote
          </a>
        </div>
      </div>
    </nav>
  );
}
